import React from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, Container, alpha } from '@mui/material';
import {
  Link as ConnectIcon,
  Psychology as AnalyzeIcon,
  TrendingUp as ActionIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { tokens } from '../../theme';

const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      number: '01',
      icon: <ConnectIcon />,
      title: 'Connect Your Tools',
      description: 'Link Clover and Stripe over OAuth, self-serve. No coding, and no IT ticket.',
      details: ['Clover and Stripe live', 'One-click OAuth', 'Tokens encrypted at rest'],
      gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
      color: '#6366F1',
    },
    {
      number: '02',
      icon: <AnalyzeIcon />,
      title: 'We Read What You Connected',
      description: 'Clover and Stripe each pull when you run a sync from their own card. Anomaly detection runs when you generate insights on the dashboard. Not real time, and only the accounts you have connected.',
      details: ['Sync when you run it, not real time', 'Anomaly detection on your sales'],
      gradient: 'linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)',
      color: '#EC4899',
    },
    {
      number: '03',
      icon: <ActionIcon />,
      title: 'See What Changed',
      description: 'The days that do not fit your pattern. We do not tell you why something happened: root cause analysis is not built.',
      details: ['Plain English insights', 'Anomaly flags on your sales'],
      gradient: 'linear-gradient(135deg, #10B981 0%, #06D6A0 100%)',
      color: '#10B981',
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: tokens.background.paper,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          height: '60%',
          background: `radial-gradient(ellipse at center, ${alpha(tokens.primary.main, 0.04)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0.15, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: tokens.primary.main,
                fontWeight: 700,
                letterSpacing: '0.1em',
                mb: 2,
                display: 'block',
              }}
            >
              How It Works
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: tokens.text.primary,
                mb: 2,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-0.03em',
              }}
            >
              Up and running{' '}
              <Box
                component="span"
                sx={{
                  background: tokens.primary.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                without an onboarding call
              </Box>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: tokens.text.secondary,
                maxWidth: 500,
                mx: 'auto',
                fontSize: '1.1rem',
                lineHeight: 1.7,
              }}
            >
              Three simple steps to transform how you understand your business.
            </Typography>
          </Box>
        </motion.div>

        {/* Steps */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: { xs: 4, lg: 3 },
            alignItems: 'stretch',
          }}
        >
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <motion.div
                initial={{ opacity: 0.15, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                style={{ flex: 1 }}
              >
                <Box
                  sx={{
                    height: '100%',
                    p: 4,
                    borderRadius: '24px',
                    background: tokens.background.subtle,
                    border: `1px solid ${tokens.border.light}`,
                    position: 'relative',
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: alpha(step.color, 0.3),
                      boxShadow: `0 20px 50px ${alpha(step.color, 0.15)}`,
                      '& .step-number': {
                        transform: 'scale(1.1)',
                        boxShadow: `0 12px 30px ${alpha(step.color, 0.4)}`,
                      },
                    },
                  }}
                >
                  {/* Step Number */}
                  <Box
                    className="step-number"
                    sx={{
                      position: 'absolute',
                      top: -16,
                      right: 24,
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      background: step.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '1rem',
                      boxShadow: `0 8px 24px ${alpha(step.color, 0.35)}`,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {step.number}
                  </Box>

                  {/* Icon */}
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '20px',
                      background: `linear-gradient(135deg, ${alpha(step.color, 0.15)} 0%, ${alpha(step.color, 0.05)} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: step.color,
                      mb: 3,
                    }}
                  >
                    {React.cloneElement(step.icon, { sx: { fontSize: 36 } })}
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: tokens.text.primary,
                      mb: 2,
                    }}
                  >
                    {step.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: tokens.text.secondary,
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    {step.description}
                  </Typography>

                  {/* Details */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {step.details.map((detail, detailIndex) => (
                      <Box
                        key={detailIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: step.gradient,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: tokens.text.secondary,
                            fontWeight: 500,
                          }}
                        >
                          {detail}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </motion.div>

              {/* Arrow Connector (not on last item) */}
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    display: { xs: 'none', lg: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1,
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.5 + index * 0.15 }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: tokens.background.subtle,
                        border: `1px solid ${tokens.border.light}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: tokens.text.secondary,
                      }}
                    >
                      <ArrowIcon sx={{ fontSize: 20 }} />
                    </Box>
                  </motion.div>
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorksSection;
